import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRightIcon, SearchIcon, XIcon } from "lucide-react";
import { products } from "../data/products";
import { formatPrice, shopifyImg } from "../utils/format";
interface SearchOverlayProps {
  open: boolean;
  onClose: () => void;
}
const suggestions = ["Tafellamp", "Vaas", "Fotolamp", "Koraal", "Mini lamp"];
export function SearchOverlay({
  open,
  onClose
}: SearchOverlayProps) {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (!open) return;
    setQuery("");
    const t = window.setTimeout(() => inputRef.current?.focus(), 50);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return products.slice(0, 4);
    return products.filter(p => `${p.name} ${p.title} ${p.type} ${p.category}`.toLowerCase().includes(q));
  }, [query]);
  const go = (to: string) => {
    onClose();
    navigate(to);
  };
  return <AnimatePresence data-id="element-392" data-mp="v16xgrhjfanj">
      {open && <div className="fixed inset-0 z-50" data-id="element-393" data-mp="v0fc09hnmtaz">
          <motion.div className="absolute inset-0 bg-ink/40" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.18
      }} onClick={onClose} data-id="element-394" data-mp="v3jmtppmknam" />
          <motion.div role="dialog" aria-modal="true" aria-label="Zoeken" className="relative m-2 rounded-3xl bg-paper" initial={{
        y: -16,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: -16,
        opacity: 0
      }} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-395" data-mp="q2m5ffcfvxa6">
            <form className="mx-auto max-w-4xl px-5 py-6 lg:py-10" onSubmit={e => {
          e.preventDefault();
          if (query.trim()) go(`/shop?q=${encodeURIComponent(query.trim())}`);
        }} data-id="element-396" data-mp="3ocxjbz2wxgpv">
              <div className="flex h-14 items-center gap-3 rounded-full bg-sand pl-5 pr-2" data-id="element-397" data-mp="fxxduxekpgwr">
                <SearchIcon className="h-5 w-5 text-muted" data-id="element-398" data-mp="bwkbixaovvul" />
                <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)} placeholder="Zoek lampen, vazen, cadeaus…" className="flex-1 bg-transparent text-lg text-ink placeholder:text-muted/70 focus:outline-none" aria-label="Zoekterm" data-id="element-399" data-mp="bxdi9y9nelcq" />
                <button type="button" onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-full bg-paper hover:bg-stone" aria-label="Zoeken sluiten" data-id="element-400" data-mp="uuo68mptxscv">
                  <XIcon className="h-5 w-5" data-id="element-401" data-mp="174vxvl2vwz7t" />
                </button>
              </div>

              <div className="mt-4 flex flex-wrap gap-2" data-id="element-402" data-mp="1c48tovbqhgmj">
                {suggestions.map(s => <button key={s} type="button" onClick={() => setQuery(s)} className="rounded-full bg-sand px-3 py-1.5 text-xs text-ink transition-colors duration-150 hover:bg-stone" data-id="element-403" data-mp="1g6vhml5aaheg">
                    {s}
                  </button>)}
              </div>

              <div className="mt-8 flex items-center justify-between" data-id="element-404" data-mp="1uv9pxblgyy2e">
                <p className="text-sm text-muted" data-id="element-405" data-mp="1uw1limf2gvso">{query ? `${results.length} resultaten` : "Populair"}</p>
                {query && results.length > 0 && <button type="submit" className="inline-flex items-center gap-1 text-sm font-medium text-ink underline underline-offset-4" data-id="element-406" data-mp="23wpsix61lvw2">
                    Alle resultaten <ArrowRightIcon className="h-3.5 w-3.5" data-id="element-407" data-mp="28soj8reufpzn" />
                  </button>}
              </div>
              {results.length === 0 ? <p className="py-10 text-[15px] text-ink" data-id="element-408" data-mp="1r679zisgaoge">Geen producten gevonden voor “{query}”. Probeer een andere zoekterm.</p> : <ul className="mt-4 grid max-h-[50vh] grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2" data-id="element-409" data-mp="1zzam9nrd0v9g">
                  {results.map(p => <li key={p.id} data-id="element-410" data-mp="1c9yd2nw18n75">
                      <button type="button" onClick={() => go(`/product/${p.id}`)} className="flex w-full items-center gap-4 rounded-2xl p-2 text-left transition-colors duration-150 hover:bg-sand" data-id="element-411" data-mp="187upj0w39s5i">
                        <img src={shopifyImg(p.image, 160)} alt="" className="h-16 w-14 rounded-lg bg-sand object-cover" data-id="element-412" data-mp="1havu2x5b3b72" />
                        <span className="min-w-0 flex-1" data-id="element-413" data-mp="2so7rap6k8vk9">
                          <span className="block truncate text-[15px] font-medium text-ink" data-id="element-414" data-mp="2spsm8fwsegin">{p.name}</span>
                          <span className="block truncate text-xs text-muted" data-id="element-415" data-mp="ei1enjl3appl">{p.title}</span>
                        </span>
                        <span className="text-sm text-ink" data-id="element-416" data-mp="5fihng3zj7b9">{formatPrice(p.price)}</span>
                      </button>
                    </li>)}
                </ul>}
            </form>
          </motion.div>
        </div>}
    </AnimatePresence>;
}